import React, { useCallback, useEffect, useState, ReactNode } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import debounce from 'lodash.debounce';

import { Autocomplete, AutocompleteResults } from '@origin-digital/ods-lab';
import { Box, Button, Divider, MarkdownLite, Stack, Text } from '@origin-digital/ods-core';
import type {
  AutocompleteInputChangeReason,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';

import { AddressResult } from '../models';
import { call, createSafeLabel, getOptionLabel } from '../helpers';
import {
  AddressManualEntryComponentProps,
  AddressManualEntryDefault,
  AddressManualEntryProps,
} from '../AddressManualEntry';
import { content } from './content';
import { isQuerySearchable } from './helpers';
import { matchSorter } from 'match-sorter';
import styled from 'styled-components';

export interface AddressLookupUncontrolledProps {
  id: string;
  fetchAddresses: (query: string) => Promise<AddressResult[]>;
  'data-id'?: string;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  value?: string;
  endIcon?: 'success' | 'error' | 'validating' | ReactNode;
  forcePopupIcon?: boolean;
  disableClearable?: boolean;
  onSelect?: (selectedOption: AddressResult) => Promise<void>;
  onError?: (error: Error) => void;
  onQueryChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  manualEntry?: boolean;
  manualEntryProps?: AddressManualEntryProps & {
    onManualEntryOpen?: () => void;
    onManualNMIOpen?: () => void;
    onManualMIRNOpen?: () => void;
    onManualLocIdOpen?: () => void;
    onManualEntryClose?: () => void;
  };
  optionalStreetType?: boolean;
  AddressManualEntryComponent?: React.JSXElementConstructor<AddressManualEntryComponentProps>;
}
export type AddressLookupControlledProps = AddressLookupUncontrolledProps &
  Required<Pick<AddressLookupUncontrolledProps, 'value' | 'onQueryChange'>>;

export type AddressLookupProps = AddressLookupUncontrolledProps | AddressLookupControlledProps;

const StyledAutocompleteResults = styled(AutocompleteResults)`
  border-radius: 0 0 6px 6px;

  // Ensure that rounded are removed from default MUI Paper component used in AutocompleteResults
  .MuiPaper-root {
    border: none;
    border-radius: 0;
  }
`;

const StyledManualAddressNmiButtons = styled(Stack)`
  padding-top: 5px;
  padding-bottom: 5px;
`;

type AutocompletePopperProps = React.ComponentProps<typeof AutocompleteResults>;

export const AddressLookup = ({
  id,
  disabled,
  placeholder = content.placeholder,
  helperText = content.helperText,
  label = content.label,
  error,
  value,
  manualEntry,
  manualEntryProps,
  endIcon,
  forcePopupIcon,
  disableClearable,
  optionalStreetType,
  onBlur,
  fetchAddresses,
  onSelect,
  onError,
  onQueryChange,
  AddressManualEntryComponent = AddressManualEntryDefault,
  ...props
}: AddressLookupProps) => {
  const [options, setOptions] = useState<AddressResult[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const enterAddressManuallyBtnRef = React.useRef<HTMLButtonElement>(null);

  const isPostcode = /^\d{4}$/.test(String(value).trim());

  const isControlled = value !== undefined;
  const dataId = props['data-id'] || id;

  const shouldFetchAddresses = isControlled && value !== '' && value.length > 3;

  const handleDelayedFetchOptions = useCallback(
    debounce(async (newInputValue: string) => {
      setLoading(true);

      try {
        const result = await fetchAddresses(newInputValue);

        if (result.length > 0) {
          setOptions(result);
        }
      } catch (error) {
        call(onError, error as Error);
      }

      setLoading(false);
    }, 400),
    []
  );

  useEffect(() => {
    onManualEntryClose();

    // clear pre-loaded options when value is cleared
    if (value === undefined || value === null || value === '') {
      setOptions([]);
    }

    (async () => {
      if (shouldFetchAddresses) {
        await handleDelayedFetchOptions(value);
      }
    })();
  }, [value]);

  const filterOptions = (options: AddressResult[], params: FilterOptionsState<AddressResult>) => {
    return matchSorter<AddressResult>(options, params.inputValue, {
      keys: ['label'],
      threshold: matchSorter.rankings.NO_MATCH,
    });
  };

  const onChange = (_: React.SyntheticEvent, newValue: AddressResult | null) => {
    if (onSelect) {
      void onSelect(newValue as AddressResult);
    }

    setInputValue(getOptionLabel(newValue ?? undefined));
  };

  const onInputChange = async (
    _: React.SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'reset') {
      return;
    }

    call(onQueryChange, newInputValue);

    setInputValue(newInputValue);

    if (!isQuerySearchable(newInputValue)) {
      return;
    }

    handleDelayedFetchOptions(newInputValue);
  };

  const onManualEntryOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualEntryOpen?.();
  };

  const onManualNMIOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualNMIOpen?.();
  };

  const onManualMIRNOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualMIRNOpen?.();
  };

  const onManualEntryClose = () => {
    setShowManualEntry(false);
    manualEntryProps?.onManualEntryClose?.();
  };

  const onManualLocIdOpen = () => {
    setShowManualEntry(true);
    setAutocompleteOpen(false);
    manualEntryProps?.onManualLocIdOpen?.();
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AddressResult) => {
    const { label } = option;

    if (!label) return null;

    const matches = match(label, inputValue);
    const parts = parse(label, matches);

    return (
      <li {...props}>
        <Text>
          <MarkdownLite>
            {parts.map(({ highlight, text }) => (highlight ? `**${text}**` : text)).join('')}
          </MarkdownLite>
        </Text>
      </li>
    );
  };

  const focusLockPopper = (
    event: React.SyntheticEvent & { relatedTarget?: EventTarget | null }
  ) => {
    const { relatedTarget } = event;

    //  Prevent autocomplete drop-down from closing
    if (
      relatedTarget &&
      (relatedTarget as HTMLElement).id === enterAddressManuallyBtnRef.current?.id
    ) {
      return;
    }

    setAutocompleteOpen(false);
  };

  // A11y ensures Manual Address entry receives focus
  const focusOnTab = (event: React.KeyboardEvent) => {
    const enterAddressBtn = enterAddressManuallyBtnRef.current;

    if (event.code === 'Tab' && autocompleteOpen && enterAddressBtn) {
      event.preventDefault();
      enterAddressBtn.focus();
    }
  };

  const renderPopper = (popperProps: AutocompletePopperProps) => {
    if (isQuerySearchable(inputValue) || isPostcode || shouldFetchAddresses) {
      return (
        <StyledAutocompleteResults {...popperProps}>
          {(inputValue?.length >= 1 || !manualEntry) && <Box {...popperProps} />}
          {manualEntry && (
            <Box>
              <Divider />
              <Box paddingX="medium" paddingY="xsmall">
                <StyledManualAddressNmiButtons space="medium" alignX="left">
                  <Button
                    noTextPadding
                    id="autocomplete-manual-address-trigger"
                    data-id="autocomplete-manual-address-trigger"
                    size="small"
                    variant="text"
                    onClick={onManualEntryOpen}
                    muiProps={{
                      ref: enterAddressManuallyBtnRef,
                      onMouseDown: (event) => event.preventDefault(),
                    }}
                  >
                    Enter address manually
                  </Button>
                  {manualEntryProps?.onManualNMIOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-nmi-trigger"
                      data-id="autocomplete-manual-nmi-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualNMIOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter NMI manually
                    </Button>
                  )}
                  {manualEntryProps?.onManualMIRNOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-mirn-trigger"
                      data-id="autocomplete-manual-mirn-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualMIRNOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter MIRN manually
                    </Button>
                  )}
                  {manualEntryProps?.onManualLocIdOpen && (
                    <Button
                      noTextPadding
                      id="autocomplete-manual-loc-id-trigger"
                      data-id="autocomplete-manual-loc-id-trigger"
                      size="small"
                      variant="text"
                      onClick={onManualLocIdOpen}
                      muiProps={{
                        ref: enterAddressManuallyBtnRef,
                        onMouseDown: (event) => event.preventDefault(),
                      }}
                    >
                      Enter LOC ID manually
                    </Button>
                  )}
                </StyledManualAddressNmiButtons>
              </Box>
            </Box>
          )}
        </StyledAutocompleteResults>
      );
    }

    return null;
  };

  const muiProps: Partial<MuiAutocompleteProps<AddressResult, false, boolean, false>> = {
    renderOption,
    loading,
    onChange,
    onInputChange,
    filterOptions,
    getOptionLabel,
    inputValue: isControlled ? value : inputValue,
    open: autocompleteOpen,
    PopperComponent: renderPopper,
    onOpen: () => setAutocompleteOpen(true),
    onClose: focusLockPopper,
    onKeyDown: focusOnTab,
    noOptionsText: isPostcode ? content.postcodeNoOptionsText : content.noOptionsText,
    blurOnSelect: 'touch' as const,
  };

  const manualProps = {
    ...manualEntryProps,
    id: `${id}-manual-entry`,
    'data-id': `${dataId}-manual-entry`,
  };

  return (
    <Stack data-id={dataId}>
      <Autocomplete<AddressResult>
        id={id}
        data-id={`${dataId}-autocomplete`}
        options={options}
        muiProps={muiProps}
        error={error}
        placeholder={createSafeLabel(placeholder)}
        label={createSafeLabel(label)}
        helperText={helperText}
        disabled={disabled}
        endIcon={endIcon}
        forcePopupIcon={forcePopupIcon}
        disableClearable={disableClearable}
        onBlur={onBlur}
      />
      {
        <AddressManualEntryComponent
          {...manualProps}
          isOpen={showManualEntry}
          onClose={onManualEntryClose}
          optionalStreetType={optionalStreetType}
        />
      }
    </Stack>
  );
};
